import { useTranslation } from 'react-i18next'
import type { ClientProfileWithCaseCount } from '../../types/case-management'
import { ClientRow, type ClientRowProps } from './ClientRow'

function TableHead(): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <thead className="bg-stone-50 dark:bg-stone-800/50">
      <tr>
        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-stone-500 uppercase dark:text-stone-400">
          {t('clients.tableClient')}
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-stone-500 uppercase dark:text-stone-400">
          {t('clients.tableIndustry')}
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-stone-500 uppercase dark:text-stone-400">
          {t('clients.tableContact')}
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-stone-500 uppercase dark:text-stone-400">
          {t('clients.tableCases')}
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-stone-500 uppercase dark:text-stone-400">
          {t('clients.tableStatus')}
        </th>
        <th className="px-6 py-3 text-right text-xs font-medium tracking-wider text-stone-500 uppercase dark:text-stone-400">
          {t('clients.tableActions')}
        </th>
      </tr>
    </thead>
  )
}

const COLUMN_COUNT = 6

interface EmptyStateProps {
  hasFilter: boolean
}

function EmptyState({ hasFilter }: EmptyStateProps): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <tr>
      <td
        colSpan={COLUMN_COUNT}
        className="px-6 py-12 text-center text-sm text-stone-500 dark:text-stone-400"
      >
        {hasFilter ? t('clients.noClientsMatch') : t('clients.noClientsYet')}
      </td>
    </tr>
  )
}

interface ClientsTableProps {
  profiles: ClientProfileWithCaseCount[]
  hasFilter: boolean
  rowProps: Omit<ClientRowProps, 'client'>
}

export function ClientsTable(props: ClientsTableProps): React.ReactElement {
  const { profiles, hasFilter, rowProps } = props
  return (
    <div className="rounded-xl border border-stone-200 bg-white dark:border-stone-800 dark:bg-stone-900">
      <table className="min-w-full divide-y divide-stone-200 dark:divide-stone-800">
        <TableHead />
        <tbody className="divide-y divide-stone-200 dark:divide-stone-800">
          {profiles.length === 0 ? (
            <EmptyState hasFilter={hasFilter} />
          ) : (
            profiles.map((client) => <ClientRow key={client.id} client={client} {...rowProps} />)
          )}
        </tbody>
      </table>
    </div>
  )
}
